const eventTypes = {
	move: 'move',
	resize: 'resize',
};

const saveKey = '__saveCaptionData__';

class Caption {
	constructor(id) {
		this.ref = null;
		this.onChangeCaption = new Function();
		this.onChangePosition = new Function();
		this.onChangeSize = new Function();

		this.__id = id;
		this.__eventType = '';

		this.__initPosition = {
			x: 0,
			y: 0,
			left: 0,
			top: 0,
		};

		this.__startPosition = null;
		this.__startSize = null;
		this.__maxPosition = null;
		this.__newPosition = {};
		this.__newSize = {};

		this.__handleMove = this.handleMove.bind(this);
		this.__end = this.end.bind(this);
		this.__resetCaption = this.resetCaption.bind(this);
		this.hasCaption = this.getSavedCaption().hasCaption;

		this.mount();
	}

	mount() {
		document.addEventListener('keydown', this.__resetCaption);
	}

	setRef(ref) {
		this.ref = ref;

		const { left, top, width, height } = this.ref?.getBoundingClientRect() || {};

		this.__initPosition = {
			x: (Number(window.innerWidth) / 2) - (Number(width) / 2),
			y: (Number(window.innerHeight) / 2) - (Number(height) / 2),
			left,
			top,
			width,
			height,
		};
	}

	unmount() {
		document.removeEventListener('keydown', this.__resetCaption);
		document.removeEventListener('mousemove', this.__handleMove);
		document.removeEventListener('mouseup', this.__end);
		document.body.style.userSelect = 'auto';
	}

	resetCaption(e) {
		if (e.altKey && e.code === 'KeyR') {
			const saved = JSON.parse(localStorage.getItem(saveKey)) || {};

			delete saved[this.__id];

			localStorage.setItem(saveKey, JSON.stringify(saved));

			this.hasCaption = false;
			this.onChangePosition({});
			this.onChangeSize({});
			this.onChangeCaption(this.hasCaption);
		}
	}

	getSavedCaption() {
		const saved = JSON.parse(localStorage.getItem(saveKey)) || {};

		if (!saved || !saved[this.__id]) {
			return {
				hasCaption: false,
				position: {},
				size: {},
			};
		}

		const _saved = saved[this.__id];

		return {
			hasCaption: true,
			position: {
				x: _saved.x,
				y: _saved.y,
			},
			size: {
				width: _saved.width,
				height: _saved.height,
			},
		};
	}

	saveCaption() {
		const saved = JSON.parse(localStorage.getItem(saveKey)) || {};

		const data = {
			...saved[this.__id] || {},
			...this.__newPosition,
			...this.__newSize,
		};

		if (!Object.keys(data).length) {
			return;
		}

		localStorage.setItem(saveKey, JSON.stringify({
			...saved,
			[this.__id]: data,
		}));
	}

	handleChangePosition(e) {
		if (typeof this.onChangePosition !== 'function') {
			return console.error(`CapturePosition onChangePosition must be a function, but got ${typeof this.onChangePosition}`);
		}

		const client = {
			x: e.clientX,
			y: e.clientY,
		};

		if (!this.__startPosition) {
			this.__startPosition = {
				x: client.x - this.__initPosition.left,
				y: client.y - this.__initPosition.top,
			};
		}

		const newPosition = {
			x: client.x - this.__startPosition.x,
			y: client.y - this.__startPosition.y,
		};

		if (!this.__maxPosition) {
			this.__maxPosition = {
				x: window.innerWidth - this.__initPosition.width,
				y: window.innerHeight - this.__initPosition.height,
			};
		}

		// min value
		newPosition.x = newPosition.x <= 0 ? 0 : newPosition.x;
		newPosition.y = newPosition.y <= 0 ? 0 : newPosition.y;

		// max value
		newPosition.x = newPosition.x > this.__maxPosition.x ? this.__maxPosition.x : newPosition.x;
		newPosition.y = newPosition.y > this.__maxPosition.y ? this.__maxPosition.y : newPosition.y;

		this.__newPosition = { ...newPosition };

		this.onChangePosition(newPosition);
	}

	handleChangeSize(e) {
		if (typeof this.onChangeSize !== 'function') {
			return console.error(`CapturePosition onChangeSize must be a function, but got ${typeof this.onChangeSize}`);
		}

		const { x, y, width, height } = this.ref?.getBoundingClientRect() || {};

		const client = {
			x: e.clientX,
			y: e.clientY,
		};

		if (!this.__startPosition) {
			this.__startPosition = {
				x: client.x,
				y: client.y,
			};
		}

		if (!this.__startSize) {
			this.__startSize = {
				width,
				height,
			};
		}

		const newSize = {
			width: this.__startSize.width + (client.x - this.__startPosition.x),
			height: this.__startSize.height + (client.y - this.__startPosition.y),
		};

		this.__newPosition = {
			x,
			y,
		};

		this.__newSize = { ...newSize };

		this.onChangePosition(this.__newPosition);
		this.onChangeSize(newSize);
	}

	handleMove(e) {
		this.hasCaption = true;
		this.onChangeCaption(this.hasCaption);

		switch (this.__eventType) {
		case eventTypes.move:
			return this.handleChangePosition(e);
		case eventTypes.resize:
			return this.handleChangeSize(e);
		}
	}

	startResize() {
		this.__eventType = eventTypes.resize;
		this.__start();
	}

	startMove() {
		this.__eventType = eventTypes.move;
		this.__start();
	}

	__start() {
		document.body.style.userSelect = 'none';
		document.addEventListener('mousemove', this.__handleMove);
		document.addEventListener('mouseup', this.__end);
	}

	end() {
		document.body.style.userSelect = 'auto';
		document.removeEventListener('mousemove', this.__handleMove);
		this.saveCaption();
		this.__eventType = '';
		this.__maxPosition = null;
		this.__startPosition = null;
		this.__startSize = null;
		this.__newSize = {};
		this.__newPosition = {};
	}
}

export default Caption;